import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaTrash, FaStar, FaRegStar, FaPlus } from "react-icons/fa";
import api from "../../../libs/axios";
import { useAuth } from "../../../provider/auth.provider";
import { useConfirm } from "../../../provider/confirm.provider";
import { SelectWithSearch, SelectOption } from "../../../utils/select-with-search";

// `locked` comes from inc_sites.is_locked — set by "Finalize Billing". A locked
// site is frozen and the API answers 423 on every write, so the write controls
// are folded into the permission flags rather than left on screen to fail.
const TeamTab: React.FC<{ siteId: string; locked?: boolean }> = ({ siteId, locked = false }) => {
  const { hasPermission } = useAuth();
  const { confirm } = useConfirm();
  const canAssign = hasPermission("IncSite", "assign") && !locked;
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<SelectOption | null>(null);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/inc/sites/${siteId}/assignments`);
      setRows(res.data?.data || []);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!selectedUser) {
      toast.error("Select a user");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/inc/sites/${siteId}/assignments`, { user_id: selectedUser.id, is_supervisor: isSupervisor });
      setSelectedUser(null);
      setIsSupervisor(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const setSupervisor = async (id: number) => {
    await api.put(`/inc/assignments/${id}`, { is_supervisor: true });
    load();
  };

  const remove = async (id: number) => {
    const row = rows.find((r) => r.id === id);
    const name = row?.user ? `${row.user.first_name || ""} ${row.user.last_name || ""}`.trim() : "This member";
    const ok = await confirm({
      title: "Remove from site team?",
      message: `${name} will no longer be on this site's team${row?.is_supervisor ? " — and the site will have no supervisor until one is set" : ""}.`,
      confirmText: "Remove",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/inc/assignments/${id}`);
      load();
    } catch {
      /* the axios interceptor already surfaced the message */
    }
  };

  return (
    <div>
      {locked && (
        <p className="text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded px-3 py-2 mb-3">
          Billing has been finalized for this site — it is locked and this tab is read-only.
        </p>
      )}
      {canAssign && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Add team member</label>
            <SelectWithSearch
              value={selectedUser}
              onChange={setSelectedUser}
              apiEndpoint="/inc/assignable-users"
              extraParams={{ site_id: siteId }}
              dataKey="users"
              labelKey="first_name"
              displayFormat={(u) => `${u.first_name || ""} ${u.last_name || ""}${u.designation?.name ? ` — ${u.designation.name}` : ""}`.trim()}
              placeholder="Search users..."
              minSearchLength={0}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
            <input type="checkbox" checked={isSupervisor} onChange={(e) => setIsSupervisor(e.target.checked)} /> Supervisor
          </label>
          <button onClick={add} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-800 rounded-md hover:bg-blue-900 disabled:opacity-60">
            <FaPlus className="w-3 h-3" /> Add
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No team members assigned.</p>
      ) : (
        <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Designation</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-center">Supervisor</th>
              {canAssign && <th className="px-3 py-2 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2">{r.user ? `${r.user.first_name || ""} ${r.user.last_name || ""}` : `#${r.user_id}`}</td>
                <td className="px-3 py-2 text-gray-600">{r.user?.designation?.name || "-"}</td>
                <td className="px-3 py-2 text-gray-600">{r.user?.role?.name || "-"}</td>
                <td className="px-3 py-2 text-center">
                  {r.is_supervisor ? (
                    <FaStar className="inline w-4 h-4 text-amber-500" title="Supervisor" />
                  ) : canAssign ? (
                    <button onClick={() => setSupervisor(r.id)} title="Make supervisor">
                      <FaRegStar className="inline w-4 h-4 text-gray-400 hover:text-amber-500" />
                    </button>
                  ) : (
                    <FaRegStar className="inline w-4 h-4 text-gray-300" />
                  )}
                </td>
                {canAssign && (
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => remove(r.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Remove">
                      <FaTrash className="w-3.5 h-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default TeamTab;
